import { Injectable, signal, inject, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, Route } from '@angular/router';
import { AuthService } from '../../Shared/Services/auth.service';
import { PERMISSIONS, REPORTS_PERMISSIONS } from '../../../Enums/permission.enum';

export interface NavItem {
  label: string;
  icon: string;
  route: string;
  exact?: boolean;
  permission?: PERMISSIONS | PERMISSIONS[];
  entitlement?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private router = inject(Router);
  private _authService = inject(AuthService);

  private readonly _currentUser = toSignal(this._authService.currentUser$, { initialValue: null });

  readonly isExpanded = signal<boolean>(true);
  readonly activeItem = signal<NavItem>({ label: 'home', icon: 'dashboard', route: '/home' });

  readonly workspaceNavItems = signal<NavItem[]>([
    { label: 'home', icon: 'home', route: '/home' },
    { label: 'users', icon: 'group', route: '/users', permission: PERMISSIONS.INTUNE_USER_READ, entitlement: 'user_management' },
    { label: 'devices', icon: 'devices', route: '/devices', permission: PERMISSIONS.DEVICE_READ, entitlement: 'device_management' },
    { label: 'autopilot_devices', icon: 'laptop_windows', route: '/autopilot-devices', permission: [PERMISSIONS.AUTOPILOT_DEVICES_READ, PERMISSIONS.AUTOPILOT_DEVICES_IMPORT], entitlement: 'autopilot' },
    { label: 'reports', icon: 'bar_chart', route: '/reports', permission: REPORTS_PERMISSIONS, entitlement: 'reports' },
  ]);

  readonly navItems = computed(() =>
    this.workspaceNavItems().filter(item =>
      (!item.permission || (this._currentUser()?.hasPermission(item.permission) ?? false)) &&
      (!item.entitlement || (this._currentUser()?.hasEntitlement(item.entitlement) ?? false))
    )
  );

  readonly showSecondaryNav = signal<boolean>(false);
  readonly isSecondaryExpanded = signal<boolean>(true);
  readonly secondaryNavTitle = signal<string>('');
  readonly secondaryNavItems = signal<NavItem[]>([]);

  setActiveItem(item: NavItem) {
    this.activeItem.set(item);
  }

  toggleSidebar() {
    this.isExpanded.update(value => !value);
  }

  expand() {
    this.isExpanded.set(true);
  }

  collapse() {
    this.isExpanded.set(false);
  }

  toggleSecondaryNav() {
    this.isSecondaryExpanded.update(value => !value);
  }

  expandSecondaryNav() {
    this.isSecondaryExpanded.set(true);
  }

  collapseSecondaryNav() {
    this.isSecondaryExpanded.set(false);
  }

  setSecondaryView(parentRoutePath: string) {
    const parentRoute = this.findRouteByPath(parentRoutePath);

    if (!parentRoute) {
      console.warn(`Route not found: ${parentRoutePath}`);
      this.clearSecondaryView();
      return;
    }

    const currentUrl = this.router.url;
    const title = parentRoute.data?.['secondaryNavTitle'] || parentRoutePath;
    const items = this.extractSecondaryNavItems(parentRoute, currentUrl);

    this.secondaryNavTitle.set(title);
    this.secondaryNavItems.set(items);
    this.showSecondaryNav.set(true);
    this.collapse();
  }

  private findRouteByPath(path: string): Route | undefined {
    const pathSegments = path.split('/').filter(segment => segment);

    const searchRoutes = (routes: Route[], segments: string[]): Route | undefined => {
      if (segments.length === 0) return undefined;

      const currentSegment = segments[0];
      const remainingSegments = segments.slice(1);

      for (const route of routes) {
        if (route.path === currentSegment) {
          // If this is the last segment, return the route
          if (remainingSegments.length === 0) {
            return route;
          }
          // Otherwise, search in children
          if (route.children) {
            const found = searchRoutes(route.children, remainingSegments);
            if (found) return found;
          }
        }

        if (route.path === '' && route.children) {
          const found = searchRoutes(route.children, segments);
          if (found) return found;
        }
      }
      return undefined;
    };

    return searchRoutes(this.router.config, pathSegments);
  }

  private extractSecondaryNavItems(parentRoute: Route, currentUrl: string): NavItem[] {
    if (!parentRoute.children) {
      return [];
    }


    let baseUrl = currentUrl.split('?')[0].split('#')[0];


    if (baseUrl.endsWith('/')) {
      baseUrl = baseUrl.slice(0, -1);
    }



    const childPaths = parentRoute.children
      .filter(child => child.path)
      .map(child => child.path);

    for (const childPath of childPaths) {
      if (baseUrl.endsWith('/' + childPath)) {
        baseUrl = baseUrl.slice(0, -(childPath!.length + 1));
        break;
      }
    }

    return parentRoute.children
      .filter(child => child.data?.['viewInSecondaryNav'] === true)
      .filter(child => {
        const permission = child.data?.['permission'];
        if (!permission) return true;
        return this._currentUser()?.hasPermission(permission) ?? false;
      })
      .map(child => ({
        label: child.data?.['label'] || child.path || '',
        icon: child.data?.['icon'] || '',
        route: `${baseUrl}/${child.path || ''}`,
        exact: child.pathMatch === 'full'
      }));
  }

  clearSecondaryView() {
    this.showSecondaryNav.set(false);
    this.secondaryNavItems.set([]);
    this.secondaryNavTitle.set('');
    this.expand();
  }
}